import { Link } from "react-router"
import { Mail01, MessageChatCircle, Phone01 } from "@untitledui/icons"

import type { GhlMessageStatus, GhlMessageType, GhlUser } from "@/lib/api/ghl-types"
import { cx } from "@/lib/utils/cx"

// Small parts every Supercharge screen shares — words, icons, chips.

export const usd = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n)
export const usdK = (n: number) => (Math.abs(n) >= 10_000 ? `$${(n / 1000).toFixed(1)}K` : usd(n))
export const pct = (x: number | null | undefined) => (x == null ? "—" : `${Math.round(x * 100)}%`)

export function relTime(iso: string | null | undefined): string {
  if (!iso) return "—"
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const h = Math.round(mins / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  if (d < 7) return `${d}d ago`
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}
export const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
export const hours = (h: number | null) => (h == null ? "—" : h < 1 ? `${Math.round(h * 60)}m` : h < 48 ? `${Math.round(h)}h` : `${Math.round(h / 24)}d`)

export const CHANNEL_WORD: Record<GhlMessageType, string> = { TYPE_SMS: "Text", TYPE_EMAIL: "Email", TYPE_CALL: "Call" }

export function ChannelIcon({ type, className }: { type: GhlMessageType; className?: string }) {
  const Icon = type === "TYPE_EMAIL" ? Mail01 : type === "TYPE_CALL" ? Phone01 : MessageChatCircle
  return <Icon className={cx("size-4", className)} aria-hidden />
}

const STATUS_WORD: Record<GhlMessageStatus, string> = {
  pending: "Pending", sent: "Sent", delivered: "Delivered", read: "Read", opened: "Opened", undelivered: "Not delivered", failed: "Failed",
}
export function StatusPill({ status }: { status: GhlMessageStatus }) {
  const bad = status === "failed" || status === "undelivered"
  const strong = status === "read" || status === "opened"
  return (
    <span
      className={cx(
        "inline-flex h-5 items-center rounded-md px-1.5 text-[10.5px] font-bold uppercase tracking-[0.06em]",
        bad ? "bg-red-50 text-red-700" : strong ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600",
      )}
    >
      {STATUS_WORD[status]}
    </span>
  )
}

export function TagChip({ tag, onRemove, to }: { tag: string; onRemove?: () => void; to?: string }) {
  const inner = (
    <span className="inline-flex h-6 items-center gap-1 rounded-md border border-zinc-200 bg-white px-1.5 text-[12px] text-zinc-700">
      {tag}
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label={`Remove tag ${tag}`} className="-mr-0.5 grid size-4 place-items-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30">
          ×
        </button>
      )}
    </span>
  )
  return to ? <Link to={to} className="hover:opacity-80">{inner}</Link> : inner
}

export function Avatar({ name, size = "md" }: { name: string; size?: "sm" | "md" | "lg" }) {
  const initials = name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("")
  return (
    <span
      className={cx(
        "grid shrink-0 place-items-center rounded-full bg-zinc-100 font-semibold text-zinc-700",
        size === "sm" && "size-7 text-[11px]",
        size === "md" && "size-9 text-[12.5px]",
        size === "lg" && "size-14 text-[18px]",
      )}
      aria-hidden
    >
      {initials || "?"}
    </span>
  )
}

export function userName(users: GhlUser[] | undefined, id: string | null | undefined): string {
  if (!id) return "Unassigned"
  return users?.find((u) => u.id === id)?.name ?? "Unassigned"
}

export function SectionHead({ title, meta, action }: { title: string; meta?: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 pb-3">
      <h2 className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">
        {title}
        {meta && <span className="ml-1.5 font-semibold text-zinc-400">· {meta}</span>}
      </h2>
      {action}
    </div>
  )
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-4 py-10 text-center text-[13.5px] text-zinc-500">{children}</p>
}

export const PILL =
  "h-9 rounded-full border px-3.5 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30"
export const pill = (on: boolean) => cx(PILL, on ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50")
export const DARK_BTN =
  "inline-flex h-10 items-center gap-1.5 rounded-lg bg-zinc-900 px-3.5 text-[13.5px] font-semibold text-white hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/40 focus-visible:ring-offset-2 disabled:opacity-40"
export const LIGHT_BTN =
  "inline-flex h-10 items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3.5 text-[13.5px] font-semibold text-zinc-900 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30 disabled:opacity-40"
export const INPUT =
  "h-10 rounded-lg border border-zinc-300 bg-white px-3 text-[14px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
